import { memo, useCallback, useRef, useState, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  EntityMenuMobile,
  Icon,
  MenuControlled,
} from "../../common/components";
import { type MenuOption } from "../../common/components/Menu/MenuControlled";
import { useSelector } from "../../common/hooks/useSelector";
import { useAppLoading } from "../../context/AppLoading/useAppLoading";
import ProjectService from "../../service/ProjectService";
import { Project, ProjectWithDesigns } from "../../types";
import { useCart } from "../../utils/CartHooks";
import { ROUTES } from "../../constant/common.constant";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import DeleteModal from "../DeleteModal/DeleteModal";
import { EditModal } from "../EditModal";
import { useAuth } from "../../common/hooks/useAuth";
import { MoveToTeamModal } from "../MoveToTeamModal";
import CreateMockupModal from "../Design/DesignCard/components/CreateMockupModal";
import { useTeam } from "../../context/Team";
import { useProject } from "../../context/Project/useProject";

interface Props {
  project: Project;
  designs: ProjectWithDesigns["designs"];
}

const ProjectCardMenu = ({ project, designs }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile } = useThemeBreakpoints();
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { setIsAppLoading } = useAppLoading();
  const { canDeleteProject = true, canRenameProject = true } = useSelector(
    (state) => state.userInfo
  );
  const { isAdmin } = useAuth();
  const { refetchTeam, refetchTeams } = useTeam();
  const { refetchProject } = useProject();
  const { fetchCartItemsAndUpdateStore } = useCart();
  const [showMoveProjectModal, setShowMoveProjectModal] = useState(false);
  const [showCreateMockupModal, setShowCreateMockupModal] = useState(false);

  const closeMenu = () => {
    setIsOpen(false);
  };

  const onRenameClose = () => {
    setShowRenameModal(false);
  };

  const onDeleteClose = () => {
    setShowDeleteModal(false);
  };

  const onRename = useCallback(
    async (name: string) => {
      try {
        await ProjectService.updateProjectName({
          id: project.id,
          name: name,
        });
        await refetchProject({ skipLoading: true });
        await refetchTeam({ skipLoading: true });
        await refetchTeams({ skipLoading: true });
      } catch (e) {
        console.error(e);
      } finally {
        closeMenu();
        setShowRenameModal(false);
      }
    },
    [refetchProject, refetchTeam, refetchTeams, project.id]
  );

  const onDelete = useCallback(async () => {
    try {
      await ProjectService.deleteProject(project?.id ?? "");

      await refetchProject({ skipLoading: true });
      await refetchTeam({ skipLoading: true });
      await refetchTeams({ skipLoading: true });
      fetchCartItemsAndUpdateStore(); //Because deleting a Project and it's designs can impact the cart
    } catch (e) {
      console.error(e);
    } finally {
      setShowDeleteModal(false);
    }
  }, [
    project?.id,
    refetchProject,
    refetchTeams,
    fetchCartItemsAndUpdateStore,
    refetchTeam,
  ]);

  const handleMenuItemClick =
    (callback: (e: MouseEvent) => void) => (e: MouseEvent) => {
      e.stopPropagation();
      callback(e);
      closeMenu();
    };

  const onDuplicateClick = useCallback(async () => {
    try {
      setIsAppLoading(true);
      const res = await ProjectService.duplicate(project?.id);
      await refetchTeams({ skipLoading: true });
      await refetchTeam({ skipLoading: true });
      setIsAppLoading(false);
      navigate(
        generatePath(ROUTES.PROJECT, {
          projectId: res.data?.data?.id,
          teamId: project?.team_id,
        })
      );
    } catch (error: any) {
      setIsAppLoading(false);
      toast.warn(
        error?.response?.data?.message ??
          "Something went wrong! Could not duplicate"
      );
    }
  }, [
    setIsAppLoading,
    project?.id,
    project?.team_id,
    refetchTeams,
    refetchTeam,
    navigate,
  ]);

  const onTurnProjectIntoTeamClick = useCallback(
    async (e: MouseEvent) => {
      e.stopPropagation();
      try {
        const res = await ProjectService.turnIntoTeam(project?.id);
        await refetchTeam({ skipLoading: true });
        await refetchProject({ skipLoading: true });
        navigate(generatePath(ROUTES.TEAM, { teamId: res.data.data.id }));
      } catch (error: any) {
        toast.warn(
          error?.response?.data?.message ??
            "Something went wrong! Could not turn project into team"
        );
      }
    },
    [project?.id, refetchTeam, refetchProject, navigate]
  );

  const options: MenuOption[] = [
    {
      labelKey: "general.rename",
      disabled: !canRenameProject,
      onClick: handleMenuItemClick(() => {
        setShowRenameModal(true);
      }),
      hideIf: isMobile,
    },
    {
      labelKey: "project.delete_menu",
      disabled: !canDeleteProject,
      onClick: handleMenuItemClick(() => setShowDeleteModal(true)),
    },
    {
      labelKey: "project.projectIntoTeam",
      onClick: onTurnProjectIntoTeamClick,
    },
    {
      labelKey: "general.duplicate",
      onClick: handleMenuItemClick(onDuplicateClick),
    },

    {
      labelKey: "project.move_to_team",
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        setShowMoveProjectModal(true);
      },
      hideIf: !isAdmin,
      adminOnly: true,
    },
    {
      labelKey: "project.download_pdf",
      onClick: (e: MouseEvent) => {
        e.stopPropagation();
        setShowCreateMockupModal(true);
      },
    },
  ];

  const favoriteDesigns = designs.filter((design) => design.isFavorite);

  return (
    <Box className="projectCardMenu">
      <Icon
        icon="more_vert"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        ref={ref}
        size={20}
        style={{
          width: "2px",
          height: "10px",
          justifyContent: "center",
          alignItems: "center",
        }}
      />

      {isMobile ? (
        <EntityMenuMobile
          isOpen={isOpen}
          onClose={closeMenu}
          options={options}
          name={project?.name}
          onSave={onRename}
          updatedAt={project.updatedAt}
        />
      ) : (
        <MenuControlled
          options={options}
          isOpen={isOpen}
          onClose={closeMenu}
          target={ref.current}
        />
      )}

      <DeleteModal
        open={showDeleteModal}
        onDelete={onDelete}
        title={t("project.delete_modal_title")}
        message={t("project.delete_modal_message")}
        onClose={onDeleteClose}
      />
      <EditModal
        open={showRenameModal}
        onSave={onRename}
        defaultValue={project.name}
        onClose={onRenameClose}
        title={t("project.rename_title")}
        ctaButtonText={t("general.rename")}
      />
      {project.teamId && (
        <CreateMockupModal
          open={showCreateMockupModal}
          onClose={() => setShowCreateMockupModal(false)}
          teamId={project.teamId}
          designs={favoriteDesigns.map((design) => ({
            designId: design.id,
            designName: design.name,
            images: design.images,
            lockedVersion: design.lockedVersion,
            family: design.family,
            product: design.product,
            productLine: design.productLine,
            decorations: design.design?.decorations,
          }))}
          projectName={project.name || ""}
        />
      )}
      <MoveToTeamModal
        open={showMoveProjectModal}
        onClose={() => setShowMoveProjectModal(false)}
        projectId={project.id}
      />
    </Box>
  );
};

export default memo(ProjectCardMenu);
